<template>
  <div class="pgs-conectar pa-4 pa-md-6">
    <!-- Encabezado -->
    <div class="mb-6">
      <h1 class="text-h4 font-weight-bold mb-1">Conecta tu asistente de IA</h1>
      <p class="text-subtitle-1 grey--text text--darken-1 mb-0">
        Consulta y opera el club en lenguaje natural desde Claude, ChatGPT o Gemini, con tu propio usuario y tus permisos.
      </p>
    </div>

    <v-alert v-if="!mcpUrl" type="warning" border="left" colored-border class="mb-6">
      La dirección del servidor aún no está configurada en este ambiente
      (<code>VUE_APP_MCP_URL</code>). Pide a soporte que la configure; mientras tanto verás
      <code>&lt;URL del servidor&gt;</code> en los pasos.
    </v-alert>

    <v-row>
      <v-col cols="12" md="8">
        <!-- Datos de conexión -->
        <v-card flat outlined class="rounded-xl pa-4 mb-6">
          <h2 class="text-h6 font-weight-bold mb-3">1. Tus datos de conexión</h2>

          <div class="mb-4">
            <div class="text-caption grey--text text--darken-1 mb-1">Dirección del servidor</div>
            <div class="pgs-copy d-flex align-center rounded-lg pa-2">
              <code class="flex-grow-1 text-body-2 pgs-copy-text">{{ mcpUrlMostrada }}</code>
              <v-btn small depressed color="primary" class="ml-2" :disabled="!mcpUrl" @click="copiar(mcpUrl)">
                <v-icon left small>mdi-content-copy</v-icon> Copiar
              </v-btn>
            </div>
          </div>

          <v-row dense>
            <v-col cols="12" sm="4">
              <div class="text-caption grey--text text--darken-1 mb-1">Campo</div>
              <div class="font-weight-bold">{{ tenant || "—" }}</div>
            </v-col>
            <v-col cols="12" sm="4">
              <div class="text-caption grey--text text--darken-1 mb-1">Usuario</div>
              <div class="font-weight-bold">El mismo con el que entras a este panel</div>
            </v-col>
            <v-col cols="12" sm="4">
              <div class="text-caption grey--text text--darken-1 mb-1">Contraseña</div>
              <div class="font-weight-bold">La misma de este panel</div>
            </v-col>
          </v-row>
          <p class="text-caption grey--text text--darken-1 mt-3 mb-0">
            Al conectar se abre una ventana de Par Golf Solutions donde escribes el código de tu campo, tu usuario
            (sin <code>@…</code>) y tu contraseña. Tu asistente nunca ve tu contraseña.
          </p>
        </v-card>

        <!-- Pasos por asistente -->
        <v-card flat outlined class="rounded-xl pa-4 mb-6">
          <h2 class="text-h6 font-weight-bold mb-2">2. Conéctalo en tu asistente</h2>
          <p class="text-body-2 grey--text text--darken-1">
            Los nombres exactos de los menús cambian según la versión y el plan de cada asistente.
          </p>

          <v-tabs v-model="tab" show-arrows>
            <v-tab v-for="a in asistentes" :key="a.id"><v-icon left small>{{ a.icono }}</v-icon>{{ a.nombre }}</v-tab>
          </v-tabs>

          <v-tabs-items v-model="tab" class="pt-4">
            <v-tab-item v-for="a in asistentes" :key="a.id">
              <v-alert v-if="a.aviso" dense text type="info" class="mb-3">{{ a.aviso }}</v-alert>
              <ol class="pgs-pasos">
                <li v-for="(p, i) in a.pasos" :key="i" class="mb-2" v-html="p"></li>
              </ol>
              <div v-if="a.codigo" class="mt-3">
                <div class="d-flex align-center justify-space-between mb-1">
                  <span class="text-caption grey--text text--darken-1">{{ a.codigoTitulo }}</span>
                  <v-btn x-small text color="primary" @click="copiar(a.codigo)"><v-icon left x-small>mdi-content-copy</v-icon>Copiar</v-btn>
                </div>
                <pre class="pgs-code rounded-lg pa-3">{{ a.codigo }}</pre>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </v-card>

        <!-- Prueba -->
        <v-card flat outlined class="rounded-xl pa-4 mb-6">
          <h2 class="text-h6 font-weight-bold mb-2">3. Pruébalo</h2>
          <p class="text-body-2 grey--text text--darken-1">Pídele cosas como:</p>
          <v-chip v-for="(e, i) in ejemplos" :key="i" outlined class="ma-1" @click="copiar(e)">
            <v-icon left small>mdi-chat-outline</v-icon>{{ e }}
          </v-chip>
          <p class="text-caption grey--text text--darken-1 mt-2 mb-0">Toca una frase para copiarla.</p>
        </v-card>

        <!-- Preguntas frecuentes -->
        <v-card flat outlined class="rounded-xl pa-2 mb-6">
          <h2 class="text-h6 font-weight-bold pa-2 mb-0">Preguntas frecuentes</h2>
          <v-expansion-panels flat accordion>
            <v-expansion-panel v-for="(f, i) in faq" :key="i">
              <v-expansion-panel-header class="font-weight-medium">{{ f.q }}</v-expansion-panel-header>
              <v-expansion-panel-content class="text-body-2" v-html="f.a"></v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card>
      </v-col>

      <v-col cols="12" md="4">
        <v-card flat outlined class="rounded-xl pa-4 mb-6">
          <h2 class="text-subtitle-1 font-weight-bold mb-3"><v-icon left color="primary">mdi-check-decagram-outline</v-icon>Qué puede hacer</h2>
          <v-list dense class="pa-0">
            <v-list-item v-for="(c, i) in capacidades" :key="i" class="px-0">
              <v-list-item-icon class="mr-3"><v-icon small color="primary">{{ c.icono }}</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-body-2 font-weight-medium">{{ c.titulo }}</v-list-item-title>
                <v-list-item-subtitle class="text-caption" style="white-space: normal">{{ c.detalle }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card>

        <v-card flat outlined class="rounded-xl pa-4 mb-6">
          <h2 class="text-subtitle-1 font-weight-bold mb-3"><v-icon left color="success">mdi-shield-lock-outline</v-icon>Seguridad</h2>
          <ul class="pgs-seguridad text-body-2">
            <li>Actúa con <strong>tus</strong> permisos: solo ve y hace lo que tú puedes en el panel.</li>
            <li>Nada se crea, cambia o cancela sin una <strong>vista previa y tu confirmación</strong>.</li>
            <li>Los reportes cubren <strong>hasta 2 días</strong>; para periodos mayores usa el panel.</li>
            <li>Correos y teléfonos de clientes se muestran enmascarados.</li>
            <li>Tu contraseña no se comparte con el asistente. Para cortar el acceso, <strong>desconecta</strong> el conector en tu asistente.</li>
          </ul>
        </v-card>
      </v-col>
    </v-row>

    <v-snackbar v-model="snack" :timeout="2000" color="success" bottom>Copiado</v-snackbar>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "ConectarAsistente",
  data() {
    return {
      tab: 0,
      snack: false,
      mcpUrl: (process.env.VUE_APP_MCP_URL || "").trim(),
      ejemplos: [
        "¿Cómo está el tee sheet de mañana?",
        "Muéstrame las reservas de hoy a nombre de Pérez",
        "¿Qué tarifas hay para el sábado a las 8:00?",
        "Dame el reporte de ventas de ayer",
        "Consulta el folio A-1042",
        "Muéstrame el manifiesto de transportación de hoy",
      ],
      capacidades: [
        { icono: "mdi-golf-tee", titulo: "Tee sheet y reservas", detalle: "Consultar, crear, mover, cancelar y bloquear horarios." },
        { icono: "mdi-chart-box-outline", titulo: "Reportes", detalle: "Ventas, transacciones, productos vendidos y reporte Z (hasta 2 días)." },
        { icono: "mdi-account-search-outline", titulo: "Clientes y folios", detalle: "Ficha del cliente, ventas y reservas por folio, vista previa del ticket." },
        { icono: "mdi-bus", titulo: "Transportación", detalle: "Manifiesto de servicios por fecha." },
        { icono: "mdi-currency-usd", titulo: "Tipo de cambio", detalle: "Consultarlo y, con permisos, modificarlo con confirmación." },
      ],
      faq: [
        {
          q: "Me dice que mis credenciales son inválidas o que no tengo acceso",
          a: "Revisa que el <strong>código del campo</strong> esté correcto (por ejemplo, en mayúsculas), que uses tu <strong>usuario sin @</strong> y que sea de personal del club. Los usuarios de mayoristas y hoteles no pueden usar esta conexión.",
        },
        {
          q: "Dice que la sesión expiró o que vuelva a conectar",
          a: "Desconecta y vuelve a conectar el servicio en tu asistente. Pasa si cambiaste tu contraseña, si tu usuario fue deshabilitado o tras mucho tiempo sin usarlo.",
        },
        {
          q: "El asistente me pide un reporte de varias semanas",
          a: "Por seguridad solo consulta hasta 2 días (por ejemplo, hoy y ayer). Para periodos más largos usa los reportes del panel.",
        },
        {
          q: "¿Puede hacer cambios sin que yo lo sepa?",
          a: "No. Cada cambio muestra primero una vista previa y solo se ejecuta cuando tú lo confirmas en la conversación. Revisa siempre la vista previa antes de aprobar.",
        },
        {
          q: "¿Cómo dejo de dar acceso?",
          a: "Desconecta o elimina el conector en la configuración de tu asistente. Cambiar tu contraseña también cierra la sesión.",
        },
      ],
    };
  },
  computed: {
    ...mapGetters(["getFirebaseApp"]),
    tenant() {
      return (this.getFirebaseApp && this.getFirebaseApp.tenant) || "";
    },
    mcpUrlMostrada() {
      return this.mcpUrl || "<URL del servidor>";
    },
    asistentes() {
      const u = this.mcpUrlMostrada;
      return [
        {
          id: "claude",
          nombre: "Claude",
          icono: "mdi-creation",
          pasos: [
            "Abre <strong>Claude</strong> (claude.ai o la app de escritorio) e ingresa a <strong>Configuración → Conectores</strong>.",
            "Elige <strong>Agregar conector personalizado</strong>.",
            `Pega la dirección del servidor: <code>${u}</code> y ponle un nombre, por ejemplo <em>Par Golf</em>.`,
            "Pulsa <strong>Conectar</strong>. Se abrirá una ventana de Par Golf Solutions: escribe tu campo, usuario y contraseña.",
            "Al volver, activa el conector en la conversación y haz tu primera pregunta.",
          ],
          aviso: "En planes de equipo, el administrador de la organización puede tener que habilitar los conectores personalizados.",
        },
        {
          id: "chatgpt",
          nombre: "ChatGPT",
          icono: "mdi-message-text-outline",
          pasos: [
            "En ChatGPT abre <strong>Configuración → Aplicaciones / Conectores</strong> y activa el <strong>modo desarrollador</strong> si hace falta.",
            "Elige <strong>Crear</strong> (nuevo conector) y pega la dirección del servidor.",
            `Dirección: <code>${u}</code>. En autenticación elige <strong>OAuth</strong>.`,
            "Continúa: se abrirá la ventana de Par Golf Solutions para iniciar sesión con tu campo, usuario y contraseña.",
            "En una conversación nueva, agrega el conector desde el menú de herramientas.",
          ],
          aviso: "Los conectores personalizados dependen de tu plan de ChatGPT y, en planes de empresa, de que tu administrador los permita.",
        },
        {
          id: "gemini",
          nombre: "Gemini",
          icono: "mdi-google",
          pasos: [
            "La app de Gemini para consumo no permite conectar servidores propios. Funciona con <strong>Gemini CLI</strong> y con productos para empresas que acepten servidores MCP.",
            "En Gemini CLI, agrega el servidor a tu archivo <code>settings.json</code> (código de abajo).",
            "Al usarlo por primera vez se abre una ventana de Par Golf Solutions: escribe tu campo, usuario y contraseña.",
          ],
          codigoTitulo: "settings.json de Gemini CLI",
          codigo: JSON.stringify({ mcpServers: { "pgs-admin": { httpUrl: u } } }, null, 2),
          aviso: "Si usas otra herramienta de Google con soporte MCP, busca la opción para agregar un servidor remoto y usa la misma dirección.",
        },
      ];
    },
  },
  methods: {
    async copiar(texto) {
      if (!texto) return;
      try {
        await navigator.clipboard.writeText(texto);
      } catch (e) {
        const t = document.createElement("textarea");
        t.value = texto;
        document.body.appendChild(t);
        t.select();
        document.execCommand("copy");
        document.body.removeChild(t);
      }
      this.snack = true;
    },
  },
};
</script>

<style scoped>
.pgs-conectar { max-width: 1200px; margin: 0 auto; }
.pgs-copy { background: rgba(127, 127, 127, 0.1); }
.pgs-copy-text { word-break: break-all; background: transparent; }
.pgs-pasos { padding-left: 1.2rem; }
.pgs-seguridad { padding-left: 1.1rem; }
.pgs-seguridad li { margin-bottom: 6px; }
.pgs-code { background: rgba(127, 127, 127, 0.12); font-size: 13px; overflow-x: auto; margin: 0; }
</style>
